import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import SentryService from '@/services/sentry/Sentry.service';
import { MutationResult } from '@/shared/types/generated';
import { TRACK_USER_ACTIVITY } from '@/shared/mutations';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { useAuth } from '@/services/auth/AuthProvider';
import {
  StorageKeys,
  StorageService,
} from '@/services/storage/Storage.service';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export function useTrackUserActivity() {
  const { isAuthenticated } = useAuth();
  const { user } = useGetUser();
  const hasTrackedActivity = useRef(false);

  const [trackUserActivity] =
    useMutation<MutationResult<'trackUserActivity'>>(TRACK_USER_ACTIVITY);

  useEffect(() => {
    const trackActivity = async () => {
      // Skip if already tracked in this session, not authenticated, or user not loaded
      if (hasTrackedActivity.current || !isAuthenticated || !user) {
        return;
      }

      try {
        // Check last trigger timestamp
        const lastTriggerTimestamp = await StorageService.getItem(
          StorageKeys.LAST_USER_ACTIVITY_TRIGGER,
        );

        const now = Date.now();
        const shouldTrigger =
          !lastTriggerTimestamp ||
          now - parseInt(lastTriggerTimestamp, 10) >= TWENTY_FOUR_HOURS_MS;

        if (!shouldTrigger) {
          __DEV__ &&
            console.log(
              'User activity tracking skipped - within 24 hour window',
            );
          hasTrackedActivity.current = true; // Mark as tracked to prevent re-checking
          return;
        }

        // Fire the trigger
        await trackUserActivity();

        // Store current timestamp
        await StorageService.setItem(
          StorageKeys.LAST_USER_ACTIVITY_TRIGGER,
          now.toString(),
        );

        hasTrackedActivity.current = true;
        __DEV__ && console.log('User activity tracked successfully');
      } catch (error) {
        SentryService.captureError('Error tracking user activity', error);
      }
    };

    trackActivity();
    // Only run this effect on initial app load when user becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);
}
